import { Machine, assign, interpret } from 'xstate';
import _get from 'lodash/get';
import _noop from 'lodash/noop';
import _sortBy from 'lodash/sortBy';

import { logDetails, dropdownInputMachine } from '..';
import { preFilterTypes } from './preFilterTypes';
import { getUniqueOptions } from './getUniqueOptions';

interface GetPreFilterMachineInput {
    /**
     * An ES6 Map of {@link PreFilterConfig} objects which determines how to set up the filter logic
     */
    preFilterConfigs: Map<string, PreFilterConfig>;
}

/**
 * Generates a state machine that is responsible for handling data filtering **before** the data is
 * passed to a `react-table` instance (or any other table implementation) - hence the name
 * "pre-filter".
 *
 * The list of data items and lists of filter dropdown options can be set in two ways:
 * 1. If the SET_ALL_ITEMS_AND_SET_DROPDOWN_OPTIONS event is sent, it will set the `allItems` list
 * and calculate `dropdownInputOptionsData` based upon the values in the `allItems` list. For each
 * field in the items in `allItems` that has a preFilterConfig, a de-duplicated list of that field's
 * values will be provided as the list of dropdown options for the filter dropdown input.
 * 2. If the SET_ALL_ITEMS event is sent, it will only set the `allItems` list without setting
 * `dropdownInputOptionsData`. A subsequent SET_DROPDOWN_OPTIONS event must be sent in order to set
 * the `dropdownInputOptionsData` before the machine will transition to its `ready` state.
 * @param input - see {@link GetPreFilterMachineInput}
 * @returns an xstate Machine instance
 */
export function getPreFilterMachine<TData extends Record<string, unknown>>(
    input: GetPreFilterMachineInput,
): PreFilterMachine {
    return Machine<PreFilterContext<TData>, PreFilterEvent<TData>>(
        {
            id: 'preFilters',
            initial: 'loading',
            context: {
                allItems: [],
                // A collection of state machines that manage each DropdownInput
                dropdownInputMachines: {},
                dropdownInputOptionsData: {},
                filteredItems: [],
                preFilterConfigs: input.preFilterConfigs,
                preFilterValues: new Map<string, PreFilterValue>(),
                refreshTime: 0,
                preFilterValueUpdates: [],
            },
            states: {
                loading: {
                    on: {
                        SET_ALL_ITEMS_AND_SET_DROPDOWN_OPTIONS: {
                            actions: ['setAllItems', 'setDropdownOptions'],
                            target: 'ready',
                        },
                        SET_ALL_ITEMS: {
                            actions: ['setAllItems'],
                        },
                        SET_DROPDOWN_OPTIONS: {
                            actions: ['setDropdownOptions'],
                            target: 'ready',
                        },
                        SET_PRE_FILTER_VALUES: {
                            actions: ['addPreFilterValueUpdate'],
                        },
                        CLEAR_PRE_FILTER_VALUES: {
                            actions: ['clearPreFilterValues'],
                        },
                    },
                },
                ready: {
                    entry: [
                        'doUpdates',
                        'calculateNextFilteredItems',
                        'startDropdownInputMachines',
                    ],
                    on: {
                        SET_ALL_ITEMS_AND_SET_DROPDOWN_OPTIONS: {
                            actions: [
                                'setAllItems',
                                'setDropdownOptions',
                                'calculateNextFilteredItems',
                                'calculateNextSelection',
                            ],
                        },
                        SET_DROPDOWN_OPTIONS: {
                            actions: [
                                'setDropdownOptions',
                                'calculateNextSelection',
                            ],
                        },
                        SET_PRE_FILTER_VALUES: {
                            actions: [
                                'setPreFilterValues',
                                'calculateNextFilteredItems',
                            ],
                        },
                        DELETE_PRE_FILTER_VALUES: {
                            actions: [
                                'deletePreFilterValues',
                                'calculateNextFilteredItems',
                            ],
                        },
                        CLEAR_PRE_FILTER_VALUES: {
                            actions: ['clearPreFilterValues'],
                        },
                        STOP_DROPDOWN_INPUT_MACHINES: {
                            actions: ['stopDropdownInputMachines'],
                        },
                    },
                },
            },
        },
        {
            actions: {
                logDetails,
                setAllItems: assign(
                    (context, event): PreFilterContext<TData> => {
                        if (
                            event.type !==
                                'SET_ALL_ITEMS_AND_SET_DROPDOWN_OPTIONS' &&
                            event.type !== 'SET_ALL_ITEMS'
                        ) {
                            return context;
                        }

                        const {
                            value: { allItems, isInitialQuery },
                        } = event;

                        let filteredItems = [...context.filteredItems];

                        if (isInitialQuery) {
                            filteredItems = allItems;
                        }

                        return {
                            ...context,
                            allItems,
                            filteredItems,
                            refreshTime: Date.now(),
                        };
                    },
                ),
                /**
                 * Set the list of options for each dropdownInputMachine. This can be done
                 * explicitly with the event type SET_DROPDOWN_OPTIONS. If the event type is
                 * SET_ALL_ITEMS_AND_SET_DROPDOWN_OPTIONS, the lists of options is calculated
                 * from the values present in context.allItems.
                 */
                setDropdownOptions: assign(
                    (context, event): PreFilterContext<TData> => {
                        if (
                            event.type !==
                                'SET_ALL_ITEMS_AND_SET_DROPDOWN_OPTIONS' &&
                            event.type !== 'SET_DROPDOWN_OPTIONS'
                        ) {
                            return context;
                        }

                        const { allItems, preFilterConfigs } = context;

                        if (event.type === 'SET_DROPDOWN_OPTIONS') {
                            // dropdownInputOptionsData is being set explicitly
                            const {
                                value: { dropdownInputOptionsData },
                            } = event;

                            const nextDropdownInputOptionsData = Object.entries(
                                dropdownInputOptionsData,
                            ).reduce(
                                (
                                    acc: DropdownInputOptionsData,
                                    [id, options],
                                ): DropdownInputOptionsData => {
                                    const sortedOptions = _sortBy(
                                        options,
                                        'label',
                                    );

                                    // If `emptyOption` is provided in the preFilterConfig, prepend
                                    // it to the list of DropdownInputOptions
                                    const emptyOption =
                                        preFilterConfigs.get(id)?.emptyOption;

                                    if (emptyOption) {
                                        acc[id] = [
                                            emptyOption,
                                            ...sortedOptions,
                                        ];
                                    } else {
                                        acc[id] = sortedOptions;
                                    }

                                    return acc;
                                },
                                {} as DropdownInputOptionsData,
                            );

                            return {
                                ...context,
                                dropdownInputOptionsData:
                                    nextDropdownInputOptionsData,
                                refreshTime: Date.now(),
                            };
                        }

                        const preFilterConfigsArray = Array.from(
                            preFilterConfigs.values(),
                        );

                        // dropdownInputOptionsData is not being set explicitly, so we must
                        // determine the list of dropdown options
                        const dropdownInputOptionsData =
                            preFilterConfigsArray.reduce(
                                (
                                    acc: Record<string, DropdownInputOption[]>,
                                    curr: PreFilterConfig,
                                ): Record<string, DropdownInputOption[]> => {
                                    const {
                                        emptyOption,
                                        renderType,
                                        dropdownOptions,
                                    } = curr;

                                    // If a list of options is provided, use it and prepend any
                                    // provided `emptyOption`.
                                    if (
                                        renderType === 'dropdown' &&
                                        dropdownOptions
                                    ) {
                                        const options = [...dropdownOptions];

                                        if (emptyOption) {
                                            options.unshift(emptyOption);
                                        }

                                        acc[curr.id] = options;

                                        return acc;
                                    }

                                    // Otherwise, use allItems to calculate a unique list of
                                    // available DropdownInputOptions for each filter ID
                                    const options = getUniqueOptions(
                                        allItems,
                                        curr,
                                    );

                                    acc[curr.id] = options;

                                    return acc;
                                },
                                {} as Record<string, DropdownInputOption[]>,
                            );

                        return {
                            ...context,
                            dropdownInputOptionsData,
                            refreshTime: Date.now(),
                        };
                    },
                ),
                /**
                 * Whenever new dropdown options are being saved, ensure that each
                 * dropdownInputMachine has a valid selectedId. If the machine was set to a
                 * selectedId that is no longer in the available options, set the selectedId to the
                 * first option in the options list. This assumes that each list has at least one
                 * item in the options list, and that the first items should be treated as the
                 * default selection.
                 */
                calculateNextSelection: assign(
                    (context, event): PreFilterContext<TData> => {
                        if (
                            event.type !==
                                'SET_ALL_ITEMS_AND_SET_DROPDOWN_OPTIONS' &&
                            event.type !== 'SET_DROPDOWN_OPTIONS'
                        ) {
                            return context;
                        }

                        const { dropdownInputOptionsData } = context;

                        Object.entries(context.dropdownInputMachines).forEach(
                            (tuple): void => {
                                const filterId = tuple[0];
                                const dropdownMachine = tuple[1];
                                const dropdownOptions =
                                    context.dropdownInputOptionsData[filterId];

                                const {
                                    state: {
                                        context: { selectedId },
                                    },
                                } = dropdownMachine;

                                const currentSelectionIsInvalid =
                                    dropdownOptions.find(
                                        (
                                            option: DropdownInputOption,
                                        ): boolean => option.id === selectedId,
                                    ) === undefined;

                                const updatedOptions =
                                    dropdownInputOptionsData[filterId];

                                dropdownMachine.send({
                                    type: 'SET_OPTIONS',
                                    value: updatedOptions,
                                });

                                // If we can't find the current selectedId in the new list of
                                // options, default to the first option in the list
                                if (currentSelectionIsInvalid) {
                                    dropdownMachine.send({
                                        type: 'SAVE',
                                        value: {
                                            optionId:
                                                updatedOptions?.[0]?.id ?? null,
                                            handleChange: _noop,
                                        },
                                    });
                                }
                            },
                        );

                        return context;
                    },
                ),
                startDropdownInputMachines: assign(
                    (context): PreFilterContext<TData> => {
                        const {
                            dropdownInputOptionsData,
                            preFilterValues,
                            preFilterConfigs,
                        } = context;

                        const dropdownInputMachines = Object.entries(
                            dropdownInputOptionsData,
                        ).reduce(
                            (
                                acc: DropdownInputMachines,
                                [filterId, options],
                            ): DropdownInputMachines => {
                                const preFilterValue =
                                    preFilterValues.get(filterId);

                                const filterValuePath =
                                    preFilterConfigs.get(
                                        filterId,
                                    )?.filterValuePath;
                                // If there is a preFilterValue for this dropdown, set the
                                // selectedId to it
                                let selectedId = null;

                                if (preFilterValue && filterValuePath) {
                                    const filterValue = _get(
                                        preFilterValue,
                                        filterValuePath,
                                    );

                                    if (filterValue) {
                                        const selectedOption = options.find(
                                            (
                                                option: DropdownInputOption,
                                            ): boolean => {
                                                const optionValue = _get(
                                                    option,
                                                    filterValuePath,
                                                );

                                                return (
                                                    optionValue === filterValue
                                                );
                                            },
                                        );

                                        if (selectedOption) {
                                            selectedId = selectedOption.id;
                                        }
                                    }
                                }

                                const service = interpret(
                                    dropdownInputMachine.withContext({
                                        didOpen: false,
                                        disabledOptionIds: new Set(),
                                        options,
                                        optionLists: [],
                                        filteredOptions: options,
                                        selectedId,
                                    }),
                                ).start();

                                acc[filterId] = service;

                                return acc;
                            },
                            {} as DropdownInputMachines,
                        );

                        return {
                            ...context,
                            dropdownInputMachines,
                        };
                    },
                ),
                setPreFilterValues: assign(
                    (context, event): PreFilterContext<TData> => {
                        if (event.type !== 'SET_PRE_FILTER_VALUES') {
                            return context;
                        }

                        const {
                            value: { preFilterValues },
                        } = event;

                        const nextPreFilterValues = new Map(
                            context.preFilterValues,
                        );

                        preFilterValues.forEach(
                            (preFilterValue: PreFilterValue): void => {
                                nextPreFilterValues.set(
                                    preFilterValue.id,
                                    preFilterValue,
                                );
                            },
                        );

                        return {
                            ...context,
                            preFilterValues: nextPreFilterValues,
                        };
                    },
                ),
                addPreFilterValueUpdate: assign(
                    (context, event): PreFilterContext<TData> => {
                        if (event.type !== 'SET_PRE_FILTER_VALUES') {
                            return context;
                        }

                        const {
                            value: { preFilterValues },
                        } = event;
                        const { preFilterValueUpdates } = context;

                        return {
                            ...context,
                            preFilterValueUpdates: [
                                ...preFilterValueUpdates,
                                preFilterValues,
                            ],
                        };
                    },
                ),
                doUpdates: assign((context): PreFilterContext<TData> => {
                    const nextPreFilterValues = new Map(
                        context.preFilterValues,
                    );

                    const { preFilterValueUpdates } = context;

                    preFilterValueUpdates.forEach(
                        (preFilterValues: PreFilterValue[]): void => {
                            preFilterValues.forEach(
                                (preFilterValue: PreFilterValue): void => {
                                    nextPreFilterValues.set(
                                        preFilterValue.id,
                                        preFilterValue,
                                    );
                                },
                            );
                        },
                    );

                    return {
                        ...context,
                        preFilterValues: nextPreFilterValues,
                        preFilterValueUpdates: [],
                    };
                }),
                deletePreFilterValues: assign(
                    (context, event): PreFilterContext<TData> => {
                        if (event.type !== 'DELETE_PRE_FILTER_VALUES') {
                            return context;
                        }

                        const nextPreFilterValues = new Map(
                            context.preFilterValues,
                        );

                        event.value.filterIds.forEach(
                            (filterId: string): void => {
                                nextPreFilterValues.delete(filterId);
                            },
                        );

                        return {
                            ...context,
                            preFilterValues: nextPreFilterValues,
                        };
                    },
                ),
                clearPreFilterValues: assign(
                    (context, event): PreFilterContext<TData> => {
                        if (event.type !== 'CLEAR_PRE_FILTER_VALUES') {
                            return context;
                        }

                        const { dropdownInputMachines, preFilterConfigs } =
                            context;

                        Object.entries(dropdownInputMachines).forEach(
                            ([filterId, machine]): void => {
                                const preFilterConfig =
                                    preFilterConfigs.get(filterId);

                                if (preFilterConfig?.emptyOption?.id) {
                                    machine.send({
                                        type: 'SET_DEFAULT',
                                        value: {
                                            optionId:
                                                preFilterConfig.emptyOption.id,
                                        },
                                    });
                                }
                            },
                        );

                        return {
                            ...context,
                            filteredItems: context.allItems,
                            preFilterValues: new Map<string, PreFilterValue>(),
                            refreshTime: Date.now(),
                        };
                    },
                ),
                calculateNextFilteredItems: assign(
                    (context): PreFilterContext<TData> => {
                        const { preFilterValues, preFilterConfigs, allItems } =
                            context;

                        const preFilterValuesArray = Array.from(
                            preFilterValues.values(),
                        );

                        // Calculate next filteredItems state by checking all existing
                        // preFilterValues against field values
                        const nextFilteredItems = allItems.filter(
                            (item: TData): boolean =>
                                preFilterValuesArray.every(
                                    (
                                        currentPreFilterValue: PreFilterValue,
                                    ): boolean => {
                                        const preFilterConfig =
                                            preFilterConfigs.get(
                                                currentPreFilterValue.id,
                                            );

                                        if (preFilterConfig === undefined) {
                                            return true;
                                        }

                                        const predicate =
                                            preFilterTypes[
                                                preFilterConfig.filterType
                                            ];

                                        const fieldValue = _get(
                                            item,
                                            preFilterConfig.fieldValuePath,
                                        );

                                        const filterValue = _get(
                                            currentPreFilterValue,
                                            preFilterConfig.filterValuePath,
                                        );

                                        if (
                                            fieldValue === undefined ||
                                            filterValue === undefined
                                        ) {
                                            return true;
                                        }

                                        return predicate(
                                            filterValue,
                                            fieldValue,
                                        );
                                    },
                                ),
                        );

                        return {
                            ...context,
                            filteredItems: nextFilteredItems,
                            refreshTime: Date.now(),
                        };
                    },
                ),
                /**
                 * This function is intended to be used to clean up the dropdownInputMachines when
                 * the PreFilters component unmounts, but I'm having difficulty calling
                 * it at the appropriate time.
                 */
                stopDropdownInputMachines: (context, event): void => {
                    if (event.type === 'STOP_DROPDOWN_INPUT_MACHINES') {
                        const { dropdownInputMachines } = context;
                        const {
                            value: { cleanup },
                        } = event;

                        Object.values(dropdownInputMachines).forEach(
                            (machine: DropdownInputMachine): void => {
                                machine.stop();
                            },
                        );

                        cleanup();
                    }
                },
            },
        },
    );
}
